import { cn } from '@talentmesh/ui/lib/utils';
import { Check } from 'lucide-react';
import UIStrings from '@/Utils/UIStrings';

export type SignupFlowStep = 'profile' | 'verify-email';

const steps = [
    { id: 'credentials', label: UIStrings.WorkEmailAndPassword },
    { id: 'profile', label: UIStrings.SetupYourProfile },
    { id: 'verify-email', label: UIStrings.VerifyYourEmail },
] as const satisfies ReadonlyArray<{ id: SignupFlowStep | 'credentials'; label: string }>;

const stepOrder: Array<SignupFlowStep | 'credentials'> = ['credentials', 'profile', 'verify-email'];

type StepStatus = 'done' | 'current' | 'upcoming';

function getStepStatus(stepId: SignupFlowStep | 'credentials', currentStep: SignupFlowStep): StepStatus {
    const stepIndex = stepOrder.indexOf(stepId);
    const currentIndex = stepOrder.indexOf(currentStep);

    if (stepIndex < currentIndex) {
        return 'done';
    }

    if (stepIndex === currentIndex) {
        return 'current';
    }

    return 'upcoming';
}

function StepMarker({ status }: { status: StepStatus }) {
    if (status === 'done') {
        return (
            <span className="bg-primary relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full">
                <Check className="size-3.5 text-white" strokeWidth={3} aria-hidden />
            </span>
        );
    }

    if (status === 'current') {
        return (
            <span className="border-primary relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full border-2 bg-white shadow-[0_0_0_5px_color-mix(in_srgb,var(--primary)_12%,transparent)]">
                <span className="bg-primary size-2.5 rounded-full" />
            </span>
        );
    }

    return <span className="relative z-10 size-7 shrink-0 rounded-full border-2 border-[#B8C6E3] bg-white" />;
}

interface SignupProgressProps {
    currentStep: SignupFlowStep;
}

function SignupProgress({ currentStep }: SignupProgressProps) {
    return (
        <aside
            aria-labelledby="signup-progress-heading"
            className="mesh-background relative mx-3 mb-3 flex items-center overflow-hidden rounded-3xl bg-[#F0F8FF] bg-size-[auto_100%] px-6 py-8 lg:my-3.5 lg:mr-3.5 lg:ml-0 lg:px-14"
        >
            <div className="relative z-10 w-full max-w-87.5">
                <p
                    id="signup-progress-heading"
                    className="text-foreground mb-7 text-[13px] font-extrabold tracking-[0.08em] uppercase"
                >
                    {UIStrings.CreatingYourAccount}
                </p>
                <ol className="relative m-0 list-none p-0">
                    <span aria-hidden className="absolute top-3.5 bottom-3.5 left-3.25 w-0.5 bg-[#C9D6F2]" />
                    {steps.map((step) => {
                        const status = getStepStatus(step.id, currentStep);

                        return (
                            <li
                                key={step.id}
                                aria-current={status === 'current' ? 'step' : undefined}
                                className={cn(
                                    'relative flex items-center gap-4 pb-7 text-lg leading-snug text-[#333F57] last:pb-0',
                                    status === 'current' && 'text-foreground font-extrabold',
                                )}
                            >
                                <StepMarker status={status} />
                                <span>{step.label}</span>
                                {status === 'done' && <span className="sr-only">({UIStrings.StepDone})</span>}
                            </li>
                        );
                    })}
                </ol>
            </div>
        </aside>
    );
}

export default SignupProgress;
